import React from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon } from 'lucide-react';
import type { ItemRecord } from '../services/smr-admin';

interface Props {
    data: ItemRecord[];
    isLoading?: boolean;
}

export const ItemDataTable: React.FC<Props> = ({ data, isLoading = false }) => {
    const navigate = useNavigate();

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading items..." />;
    }

    const columns: ColumnDef<ItemRecord>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'code',
            header: 'Code',
            cell: (info) => <span className="font-mono text-sm">{(info.getValue() as string) || '—'}</span>,
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: (info) => <span className="text-sm">{(info.getValue() as string) || '—'}</span>,
        },
        {
            accessorKey: 'created_at',
            header: 'Created',
            cell: (info) => <span className="text-sm">{new Date(info.getValue() as string).toLocaleDateString()}</span>,
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex space-x-2">
                    <Tooltip content="View item">
                        <TextLink
                            onClick={() => navigate(`/smr-admin/items/${info.row.original.id}`)}
                            color="primary"
                            startIcon={<EyeIcon size={20} />}
                        />
                    </Tooltip>
                    <PermissionGuard permission={{ resource: 'smr', action: 'item.edit' }}>
                        <Tooltip content="Edit item">
                            <TextLink
                                onClick={() => navigate(`/smr-admin/items/${info.row.original.id}`)}
                                color="secondary"
                                startIcon={<EditIcon size={18} />}
                            />
                        </Tooltip>
                    </PermissionGuard>
                </div>
            ),
        },
    ];

    return <Table<ItemRecord> data={data} columns={columns} enableSorting={false} />;
};
